import { FC, useContext, useEffect, useRef } from 'react';
import { DpCatalogEntryCarouselHeader } from '@/components/blocks/DpLibrary/DpCatalogEntryCarouselHeader';
import { DpIARecommendationHeader } from '@/components/content/CatalogEntryIARecommendation/parts/DpIARecommendationHeader';
import { NullElement } from '@/components/blocks/NullElement';
import { CatalogEntryIARecommendationSkeleton } from '@/components/content/CatalogEntryIARecommendation/parts/CatalogEntryIARecommendationSkeleton';
import { DpIARecommendationResponse } from 'integration/generated/transactions/data-contracts';
import { ProductType } from '@/data/types/Product';
import { CLICK_PRODUCT_VIEW_SOURCE } from '@/data/constants/clickProductView';
import { useCatalogEntryIARecommendation } from '@/data/Content/CatalogEntryIARecommendation';
import { useSettings } from '@/data/Settings';
import { EventsContext } from '@/data/context/events';
import { useCatalogEntryIARecommendationItems } from '@/data/Content/CatalogEntryIARecommendationItems';
import { ContentProvider } from '@/data/context/content';
import { CatalogEntryCarousel } from '@/components/content/CatalogEntryRecommendation/parts/CatalogEntryCarousel';
import { usePDPContext } from '@/data/context/PDP';
import { catalogEntryOrderIaRecommendation } from '@/utils/catalogEntryOrder';

export const CatalogEntryIARecommendation: FC<{
	id?: string;
	title?: string;
	params: {
		partNumber?: string;
	};
	transform?: {
		order: string;
	} | null;
	loadingElement?: JSX.Element;
	emptyElement?: JSX.Element;
	partNumbersExtractor: (data: DpIARecommendationResponse) => string[];
	emptyExtractor: (products: ProductType[]) => boolean;
	highlightFirstProduct?: boolean;
	showRecommendationBadge?: boolean;
	clickSource?: CLICK_PRODUCT_VIEW_SOURCE;
	filters?: Array<any>;
}> = ({
	params,
	transform,
	title,
	id = 'catalogEntryRecommendationIA',
	loadingElement,
	emptyElement,
	emptyExtractor,
	partNumbersExtractor,
	highlightFirstProduct = false,
	showRecommendationBadge = false,
	clickSource,
	filters,
}) => {
	const { products, contract, isLoadingDpIARecommendation, isLoadingProducts } =
		useCatalogEntryIARecommendation({
			params,
			partNumbersExtractor,
			filters,
			orderExtractor: transform?.order
				? (prods, dataIA) => catalogEntryOrderIaRecommendation(transform.order, prods, dataIA)
				: undefined,
		});

	const { onCarouselItemListView } = useContext(EventsContext);
	const { settings } = useSettings();

	const { slides, onNotify } = useCatalogEntryIARecommendationItems({
		products,
		isLoadingProducts,
		parentPartNumber: params?.partNumber,
		variant: 'large',
		clickSource,
		contract,
		params,
		transforms: {
			includePartNumber: highlightFirstProduct,
			highlightedPartNumber: highlightFirstProduct ? params?.partNumber : undefined,
		},
	});

	const { product: parentProduct } = usePDPContext();

	const trackedKeyRef = useRef<string | null>(null);
	useEffect(() => {
		if (isLoadingProducts || !products?.length || !settings) return;

		const item_list_id = clickSource ?? id;
		const key = `${item_list_id}|${products.map((p) => p.partNumber).join(',')}`;
		if (trackedKeyRef.current === key) return;
		trackedKeyRef.current = key;

		onCarouselItemListView({
			gtm: {
				parentPartNumber: params?.partNumber,
				parentProduct,
				products,
				item_list_id,
				settings,
			},
		});
	}, [
		products,
		isLoadingProducts,
		params?.partNumber,
		clickSource,
		id,
		title,
		settings,
		onCarouselItemListView,
	]);

	if (isLoadingDpIARecommendation) {
		return loadingElement || <CatalogEntryIARecommendationSkeleton id={id} title={title} />;
	}

	if (!isLoadingProducts && emptyExtractor?.(products)) {
		return emptyElement || <NullElement />;
	}

	return (
		<>
			{title ? (
				showRecommendationBadge ? (
					<DpIARecommendationHeader title={title} showRecommendationBadge />
				) : (
					<DpCatalogEntryCarouselHeader title={title} />
				)
			) : null}
			<ContentProvider value={{ onNotify }}>
				<CatalogEntryCarousel slides={slides} id={id.toString()} />
			</ContentProvider>
		</>
	);
};
